import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDoc,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

export default function ListingLogs() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'listings'), orderBy('timestamp', 'desc'));
    const unsub = onSnapshot(q, async (snap) => {
      const data = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const listing = {
            ...data,
            id: docSnap.id,
            listingId: docSnap.id,
          };

          // Fetch user profile for UID
          if (listing.ownerId) {
            try {
              const userSnap = await getDoc(doc(db, 'users', listing.ownerId));
              if (userSnap.exists()) {
                const user = userSnap.data();
                listing.userName = user.name || '—';
                listing.userEmail = user.email || '—';
                listing.userId = listing.ownerId;
              }
            } catch (err) {
              console.warn('⚠️ User fetch failed:', err);
            }
          }

          return listing;
        })
      );
      setLogs(data);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const isInsuranceCategory = (category) => {
    return ['vehicle', 'vehicles', 'property', 'storage'].includes(category?.toLowerCase?.());
  };

  const filteredLogs = logs.filter((log) => {
    const match =
      (log.title?.toLowerCase?.() || '').includes(filter.toLowerCase()) ||
      (log.userName?.toLowerCase?.() || '').includes(filter.toLowerCase()) ||
      (log.userEmail?.toLowerCase?.() || '').includes(filter.toLowerCase());

    const date = log.timestamp?.toDate?.();
    const matchDate =
      (!dateRange.from || new Date(dateRange.from) <= date) &&
      (!dateRange.to || date <= new Date(dateRange.to));

    return match && matchDate;
  });

  const exportCSV = () => {
    const headers = ['#', 'Date', 'User', 'UID', 'Email', 'Title', 'Category', 'Protection', 'Insurance', 'Status'];
    const rows = filteredLogs.map((log, i) => {
      const d = log.timestamp?.toDate?.();
      const isInsurance = isInsuranceCategory(log.category);
      return [
        i + 1,
        d?.toLocaleDateString() || '',
        log.userName || '—',
        log.userId || '—',
        log.userEmail || '—',
        log.title || '—',
        log.category || '—',
        isInsurance ? '—' : (log.protectionPlan || '—'),
        isInsurance ? (log.insuranceOption || '—') : '—',
        log.isHidden ? 'Hidden' : 'Active'
      ];
    });
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `listing_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handleHideListing = async (listingId) => {
    try {
      await updateDoc(doc(db, 'listings', listingId), { isHidden: true });
      alert('✅ Listing hidden');
    } catch (err) {
      console.error('❌ Failed to hide listing:', err);
    }
  };

  const handleUnhideListing = async (listingId) => {
    try {
      await updateDoc(doc(db, 'listings', listingId), { isHidden: false });
      alert('✅ Listing unhidden');
    } catch (err) {
      console.error('❌ Failed to unhide listing:', err);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'listings', listingId));
        alert('🗑 Listing deleted');
      } catch (err) {
        console.error('❌ Failed to delete listing:', err);
      }
    }
  };

  const handlePreview = async (listingId) => {
    try {
      const snap = await getDoc(doc(db, 'listings', listingId));
      if (snap.exists()) {
        setPreview(snap.data());
      }
    } catch (err) {
      console.error('❌ Preview error:', err);
    }
  };

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>🧾 User Listing Logs</h2>

      <input
        placeholder="Search by user, email, or title..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={styles.search}
      />
      <div style={{ display: 'flex', gap: '1rem', margin: '0.5rem 0' }}>
        <input type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} style={styles.search} />
        <input type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} style={styles.search} />
        <button onClick={exportCSV} style={styles.button}>📤 Export CSV</button>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Thumbnail</th>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>User</th>
            <th style={styles.th}>UID</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Protection</th>
            <th style={styles.th}>Insurance</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredLogs.map((log) => {
            const isInsurance = isInsuranceCategory(log.category);
            const imageUrl = log.mediaImageNames?.[0];
            return (
              <tr key={log.id} style={styles.row}>
                <td style={styles.td}>
                  {imageUrl ? (
                    <img src={imageUrl} alt="Thumbnail" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                  ) : '—'}
                </td>
                <td style={styles.td}>{log.timestamp?.toDate?.().toLocaleDateString() || '—'}</td>
                <td style={styles.td}>{log.userName || '—'}</td>
                <td style={styles.td}>{log.userId || '—'}</td>
                <td style={styles.td}>{log.userEmail || '—'}</td>
                <td style={styles.td}>{log.title || '—'}</td>
                <td style={styles.td}>{log.category || '—'}</td>
                <td style={styles.td}>{isInsurance ? '—' : (log.protectionPlan || '—')}</td>
                <td style={styles.td}>{isInsurance ? (log.insuranceOption || '—') : '—'}</td>
                <td style={styles.td}>{log.isHidden ? 'Hidden' : 'Active'}</td>
                <td style={styles.td}>
                  <button onClick={() => handlePreview(log.listingId)} style={{ marginRight: 4 }}>👁 View</button>
                  {log.isHidden ? (
                    <button onClick={() => handleUnhideListing(log.listingId)} style={{ marginRight: 4 }}>🙈 Unhide</button>
                  ) : (
                    <button onClick={() => handleHideListing(log.listingId)} style={{ marginRight: 4 }}>🙈 Hide</button>
                  )}
                  <button onClick={() => handleDeleteListing(log.listingId)}>🗑 Delete</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {preview && (
        <div style={styles.previewBox}>
          <h3>🔍 Listing Preview</h3>
          <p><strong>Title:</strong> {preview.title || '—'}</p>
          <p><strong>Description:</strong> {preview.description || '—'}</p>
          <p><strong>Price:</strong> ${preview.pricePerDay ?? preview.price ?? 0}</p>
          <p><strong>Status:</strong> {preview.isHidden ? 'Hidden' : 'Active'}</p>
          <button onClick={() => setPreview(null)} style={styles.button}>❌ Close</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    marginBottom: '1.5rem',
    padding: '0.5rem',
    fontSize: '0.95rem',
    width: '100%',
    maxWidth: '320px',
  },
  button: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#16a34a',
    color: '#fff',
    cursor: 'pointer'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    backgroundColor: '#e0f2f1',
    fontWeight: '600',
    color: '#000',
    fontSize: '0.875rem',
  },
  td: {
    padding: '12px',
    fontSize: '0.875rem',
    color: '#333',
    verticalAlign: 'middle'
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  previewBox: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    marginTop: '2rem',
    border: '1px solid #ccc',
    borderRadius: '8px'
  }
};
